import React from "react";
import {
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import API from "../API";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { DateRangePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs from "dayjs";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";

const VEHICLE_OPTIONS = [
  { value: 0, label: "Motorsiklet" },
  { value: 2, label: "Doblo" },
  { value: 4, label: "Panelvan" },
  { value: 5, label: "Kamyonet" },
  { value: 7, label: "Kamyon (6 Teker)" },
  { value: 8, label: "Kamyon (8 Teker)" },
  { value: 9, label: "Kamyon (10 Teker)" },
];

const shortcutsItems = [
  {
    label: "Bugün",
    getValue: () => {
      const today = dayjs();
      return [today.startOf("day"), today.endOf("day")];
    },
  },
  {
    label: "Bu Hafta",
    getValue: () => {
      const today = dayjs();
      return [today.startOf("week"), today.endOf("week")];
    },
  },
  {
    label: "Geçen Hafta",
    getValue: () => {
      const today = dayjs();
      const prevWeek = today.subtract(7, "day");
      return [prevWeek.startOf("week"), prevWeek.endOf("week")];
    },
  },
  {
    label: "Son 7 Gün",
    getValue: () => {
      const today = dayjs();
      return [today.subtract(7, "day"), today];
    },
  },
  {
    label: "Bu Ay",
    getValue: () => {
      const today = dayjs();
      return [today.startOf("month"), today.endOf("month")];
    },
  },
  { label: "Her zaman", getValue: () => [null, null] },
];

const PRIORITY_CITY_IDS = [34, 35, 6, 16, 9, 54, 41, 48];

function getCityOptions() {
  const cities = Object.values(API.allowedCities || {});
  return cities.sort((a, b) => {
    const aPriority = PRIORITY_CITY_IDS.indexOf(a.id);
    const bPriority = PRIORITY_CITY_IDS.indexOf(b.id);

    if (aPriority !== -1 || bPriority !== -1) {
      if (aPriority === -1) return 1;
      if (bPriority === -1) return -1;
      return aPriority - bPriority;
    }

    return a.name.localeCompare(b.name, "tr");
  });
}

function getStatusLabel(status) {
  return status === 1 ? "ONAY BEKLİYOR" : "BAŞVURU YAPILIYOR";
}

export default class ApplicationsScreen extends React.Component {
  constructor() {
    super();
    this.state = {
      rows: [],
      ownerStats: {},
      analytics: {
        approved: 0,
        pending: 0,
        inProgress: 0,
        myApproved: 0,
      },
      selectedCity: "",
      selectedVehicle: "",
      selectedStatus: "",
      selectedOwner: "",
      claimingId: "",
    };

    this._idFilter = null;
    this.dateFilter = null;
    this.allRows = [];
  }

  componentDidMount() {
    this.loadApplications();
  }

  loadApplications = () => {
    API.fetch("getapplications").then((response) => {
      if (response.error) return;

      const applications = Array.isArray(response)
        ? response
        : response.applications || [];

      const ownerStats = response.ownerStats || {};

      this.allRows = applications.map((application, index) => {
        const createdDate = dateFromObjectId(application._id);
        const isCompleted = application.submitDate && application.status === 1;

        return {
          id: application._id || index,
          application,
          createdAt: createdDate,
          createdAtText: createdDate.toLocaleString("tr-TR"),
          completedAt: isCompleted ? application.submitDate : 0,
          completedAtText: isCompleted
            ? new Date(application.submitDate).toLocaleString("tr-TR")
            : "",
          status: application.status,
          note: application.note,
          phone: application.tel,
          cityId: application.city,
          cityName: API.getCityName(application.city),
          vehicleType: Number(application.type),
          vehicleName:
            VEHICLE_OPTIONS.find(
              (vehicle) => vehicle.value === Number(application.type),
            )?.label || "-",
          tFile: application.tFile,
          lFile: application.lFile,
          caseOwnerId: application.caseOwnerId || "",
          caseOwnerName: getOwnerName(application, ownerStats),
        };
      });

      const approved = Object.values(ownerStats).reduce(
        (sum, stats) => sum + (stats.approved || 0),
        0,
      );
      const pending = this.allRows.filter((row) => Number(row.status) === 1).length;
      const inProgress = this.allRows.filter(
        (row) => Number(row.status) === 0,
      ).length;
      const myApproved = getCurrentUserApprovedCount(ownerStats);

      this.setState(
        {
          ownerStats,
          analytics: {
            approved,
            pending,
            inProgress,
            myApproved,
          },
        },
        this.refreshCurrentFilters,
      );
    });
  };

  refreshCurrentFilters = () => {
    const { selectedCity, selectedVehicle, selectedStatus, selectedOwner } =
      this.state;

    const rows = this.allRows.filter((row) => {
      if (
        this._idFilter &&
        (row.createdAt < this._idFilter.$gte || row.createdAt > this._idFilter.$lte)
      ) {
        return false;
      }

      if (
        this.dateFilter &&
        (row.completedAt < this.dateFilter.$gte || row.completedAt > this.dateFilter.$lte)
      ) {
        return false;
      }

      if (selectedCity && String(row.cityId) !== String(selectedCity)) {
        return false;
      }

      if (
        selectedVehicle !== "" &&
        Number(row.vehicleType) !== Number(selectedVehicle)
      ) {
        return false;
      }

      if (selectedStatus !== "" && Number(row.status) !== Number(selectedStatus)) {
        return false;
      }

      if (
        selectedOwner &&
        String(row.caseOwnerId || "") !== String(selectedOwner)
      ) {
        return false;
      }

      return true;
    });

    this.setState({ rows });
  };

  handleDateRange = (key, range) => {
    if (
      !((range[0] == null && range[1] == null) || (range[0] != null && range[1] != null))
    ) {
      return;
    }

    if (range[0] != null) {
      const start = new Date(range[0].$d);
      start.setHours(0, 0, 0);

      const end = new Date(range[1].$d);
      end.setHours(23, 59, 59);

      this[key] = {
        $gte: key === "_idFilter" ? start : start.getTime(),
        $lte: key === "_idFilter" ? end : end.getTime(),
      };
    } else {
      this[key] = null;
    }

    this.refreshCurrentFilters();
  };

  handleClaim = (row) => {
    if (this.state.claimingId) return;
    if (Number(row.status) !== 0 || row.caseOwnerId) return;

    this.setState({ claimingId: row.id });
    API.fetch("setapplicationowner", { _id: row.application._id }).then((response) => {
      this.setState({ claimingId: "" });
      if (response.error) return;
      this.loadApplications();
    });
  };

  renderOwnerSummary() {
    const { selectedOwner, ownerStats } = this.state;
    if (!selectedOwner) return null;

    const stats = ownerStats[selectedOwner];
    if (!stats) return null;

    return (
      <div
        style={{
          marginTop: 16,
          marginBottom: 8,
          padding: "10px 14px",
          borderRadius: 8,
          backgroundColor: "#EEF2FF",
          color: "#3730A3",
          fontWeight: 600,
        }}
      >
        {`${stats.name} • ${stats.owned || 0} başvuru • ${stats.approved || 0} onay`}
      </div>
    );
  }

  render() {
    const ownerOptions = Object.entries(this.state.ownerStats)
      .map(([id, stats]) => ({ id, ...stats }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
    const cityOptions = getCityOptions();
    const { analytics } = this.state;

    return (
      <div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <AnalyticsCard label="Toplam Onaylanan" value={analytics.approved} />
          <AnalyticsCard label="Onay Bekleyen" value={analytics.pending} />
          <AnalyticsCard label="Başvuru Yapılıyor" value={analytics.inProgress} />
          <AnalyticsCard
            label="Benim Onayladıklarım"
            value={analytics.myApproved}
          />
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div>
              <p>Oluşturma Tarihi</p>
              <DateRangePicker
                onAccept={(range) => this.handleDateRange("_idFilter", range)}
                slotProps={{
                  shortcuts: { items: shortcutsItems },
                  actionBar: { actions: [] },
                }}
                slots={{ field: SingleInputDateRangeField }}
              />
            </div>

            <div>
              <p>Tamamlama Tarihi</p>
              <DateRangePicker
                onAccept={(range) => this.handleDateRange("dateFilter", range)}
                slotProps={{
                  shortcuts: { items: shortcutsItems },
                  actionBar: { actions: [] },
                }}
                slots={{ field: SingleInputDateRangeField }}
              />
            </div>
          </LocalizationProvider>

          <FilterSelect
            label="Şehir"
            value={this.state.selectedCity}
            onChange={(value) =>
              this.setState({ selectedCity: value }, this.refreshCurrentFilters)
            }
            options={cityOptions.map((city) => ({
              value: city.id,
              label: city.name,
            }))}
            emptyLabel="Tüm Şehirler"
          />

          <FilterSelect
            label="Araç Tipi"
            value={this.state.selectedVehicle}
            onChange={(value) =>
              this.setState({ selectedVehicle: value }, this.refreshCurrentFilters)
            }
            options={VEHICLE_OPTIONS}
            emptyLabel="Tüm Araçlar"
          />

          <FilterSelect
            label="Durum"
            value={this.state.selectedStatus}
            onChange={(value) =>
              this.setState({ selectedStatus: value }, this.refreshCurrentFilters)
            }
            options={[
              { value: 0, label: "Başvuru Yapılıyor" },
              { value: 1, label: "Onay Bekliyor" },
            ]}
            emptyLabel="Tümü"
          />

          <FilterSelect
            label="Sorumlu Operatör"
            value={this.state.selectedOwner}
            onChange={(value) =>
              this.setState({ selectedOwner: value }, this.refreshCurrentFilters)
            }
            options={ownerOptions.map((owner) => ({
              value: owner.id,
              label: owner.name,
            }))}
            emptyLabel="Tümü"
          />
        </div>

        <Badge style={{ marginTop: 32, padding: 0 }}>
          <div style={{ padding: 24 }}>
            <Typography variant="h6">Başvurular</Typography>
            {this.renderOwnerSummary()}
          </div>
          <DataGrid
            disableSelectionOnClick
            autoHeight
            sortingOrder={["desc", "asc"]}
            initialState={{
              sorting: {
                sortModel: [{ field: "createdAt", sort: "asc" }],
              },
            }}
            rows={this.state.rows}
            columns={[
              {
                field: "application",
                headerName: "AD SOYAD",
                flex: 1.2,
                renderCell: (params) => (
                  <div>
                    <Typography variant="body1">
                      {`${params.value.firstName || ""} ${params.value.lastName || ""}`}
                    </Typography>
                    <Typography variant="body2">
                      {params.value.email || "-"}
                    </Typography>
                  </div>
                ),
              },
              {
                field: "createdAt",
                headerName: "TARİH",
                flex: 1,
                renderCell: (params) => params.row.createdAtText,
                sortComparator: (a, b) => a - b,
              },
              {
                field: "completedAt",
                headerName: "TAMAMLAMA TARİHİ",
                flex: 1,
                renderCell: (params) => params.row.completedAtText,
                sortComparator: (a, b) => a - b,
              },
              {
                field: "status",
                headerName: "DURUM",
                flex: 1.1,
                renderCell: (params) => (
                  <div
                    style={{
                      padding: "5px 10px",
                      borderRadius: 20,
                      width: "fit-content",
                      backgroundColor: params.value === 0 ? "#F59E0B" : "#16A34A",
                    }}
                  >
                    <Typography
                      sx={{ color: "white", fontWeight: "700" }}
                      variant="body2"
                    >
                      {`${getStatusLabel(params.value)} (${params.row.lFile}/${params.row.tFile})`}
                    </Typography>
                  </div>
                ),
              },
              { field: "note", headerName: "NOT", flex: 1 },
              { field: "phone", headerName: "TELEFON", flex: 1 },
              { field: "cityName", headerName: "ŞEHİR", flex: 0.8 },
              { field: "vehicleName", headerName: "ARAÇ", flex: 1 },
              {
                field: "caseOwnerName",
                headerName: "SORUMLU OPERATÖR",
                flex: 1.1,
                renderCell: (params) => {
                  if (params.value) {
                    return params.value;
                  }

                  if (params.row.caseOwnerId) {
                    return "Operatör";
                  }

                  if (Number(params.row.status) === 0) {
                    return (
                      <Button
                        size="small"
                        onClick={() => this.handleClaim(params.row)}
                        disabled={this.state.claimingId === params.row.id}
                      >
                        Üstüne Al
                      </Button>
                    );
                  }

                  return "Atanmamış";
                },
              },
              {
                field: "open",
                headerName: "",
                sortable: false,
                width: 70,
                renderCell: (params) => (
                  <a href={`../application/${params.row.application._id}`}>
                    <Button>
                      <ArrowForwardIcon />
                    </Button>
                  </a>
                ),
              },
            ]}
            pageSize={50}
            rowsPerPageOptions={[50]}
            slotProps={{
              pagination: { showFirstButton: true, showLastButton: true },
            }}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F3F4F6",
                borderRadius: 0,
              },
            }}
          />
        </Badge>
      </div>
    );
  }
}

function FilterSelect({ label, value, onChange, options, emptyLabel }) {
  return (
    <div>
      <p>{label}</p>
      <FormControl size="large" style={{ minWidth: 200 }}>
        <InputLabel id={`${label}-label`}>{label}</InputLabel>
        <Select
          labelId={`${label}-label`}
          label={label}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          <MenuItem value="">
            <em>{emptyLabel}</em>
          </MenuItem>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}

function getOwnerName(application, ownerStats) {
  if (application.caseOwnerName) return application.caseOwnerName;

  const ownerId = `${application.caseOwnerId || ""}`;
  if (!ownerId) return "";

  return ownerStats[ownerId]?.name || "";
}

function dateFromObjectId(objectId) {
  return new Date(parseInt(objectId.substring(0, 8), 16) * 1000);
}

const Badge = (props) => {
  return (
    <div
      style={{
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        boxShadow:
          "rgb(100 116 139 / 6%) 0px 1px 1px, rgb(100 116 139 / 10%) 0px 1px 2px",
        borderRadius: 8,
        padding: 24,
        color: "rgb(18, 24, 40)",
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
};

function AnalyticsCard({ label, value }) {
  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: 10,
        padding: "16px 18px",
        boxShadow:
          "rgb(100 116 139 / 6%) 0px 1px 1px, rgb(100 116 139 / 10%) 0px 1px 2px",
      }}
    >
      <Typography
        variant="caption"
        style={{ color: "#6B7280", fontSize: 12, fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography
        variant="h4"
        style={{ marginTop: 8, fontWeight: 700, color: "#111827" }}
      >
        {value}
      </Typography>
    </div>
  );
}

function getCurrentUserApprovedCount(ownerStats) {
  const currentUserId = `${API.user?._id || ""}`;
  if (currentUserId && ownerStats[currentUserId]) {
    return ownerStats[currentUserId].approved || 0;
  }

  const currentUserName = `${API.user?.name || ""}`.trim().toLowerCase();
  if (!currentUserName) return 0;

  const matchedStats = Object.values(ownerStats).find(
    (stats) => `${stats.name || ""}`.trim().toLowerCase() === currentUserName,
  );

  return matchedStats?.approved || 0;
}
